import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Copy, Loader2, Mic, PhoneOff, Sparkles, Users } from "lucide-react";

const LANGS = [["es", "Spanish"], ["fr", "French"], ["de", "German"], ["it", "Italian"],
               ["pt", "Portuguese"], ["vi", "Vietnamese"], ["ja", "Japanese"], ["ko", "Korean"],
               ["zh", "Chinese"], ["ar", "Arabic"], ["en", "English"]];

// Two friends, one room, MiLu listening in and chipping in every few turns.
export default function Buddy() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [lang, setLang] = useState("es");
  const [busy, setBusy] = useState(false);
  const [taping, setTaping] = useState(false);
  const [report, setReport] = useState(null);
  const [typed, setTyped] = useState("");
  const gear = useRef({});
  const feed = useRef(null);

  const pull = useCallback(async () => {
    if (!code) return;
    try {
      const { data } = await api.get(`/wingman/buddy/${code}`);
      setRoom(data);
      if (data.report) setReport(data.report);
    } catch (e) {
      if (e?.response?.status === 403) {
        await api.post(`/wingman/buddy/${code}/join`).then(({ data }) => setRoom(data))
          .catch(() => toast.error("That practice room has closed"));
      }
    }
  }, [code]);

  useEffect(() => {
    if (!code) return undefined;
    api.post(`/wingman/buddy/${code}/join`).then(({ data }) => setRoom(data)).catch(() => pull());
    const id = setInterval(pull, 2600);
    return () => {
      clearInterval(id);
      gear.current.stream?.getTracks().forEach((t) => t.stop());
    };
  }, [code, pull]);

  useEffect(() => {
    feed.current?.scrollTo({ top: feed.current.scrollHeight, behavior: "smooth" });
  }, [room?.lines?.length]);

  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wingman/buddy", { lesson_lang: lang, hard: true });
      nav(`/buddy/${data.code}`);
    } catch {
      toast.error("Could not open a practice room");
    } finally {
      setBusy(false);
    }
  };

  const say = async (text) => {
    if (!text.trim()) return;
    try {
      await api.post(`/wingman/buddy/${code}/say`, { text: text.trim() });
      pull();
    } catch {
      toast.error("That did not get through");
    }
  };

  const talk = async () => {
    if (taping) {
      gear.current.recorder?.state === "recording" && gear.current.recorder.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      gear.current = { stream, recorder };
      const bits = [];
      recorder.ondataavailable = (e) => e.data.size && bits.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTaping(false);
        setBusy(true);
        try {
          const form = new FormData();
          form.append("audio", new Blob(bits, { type: "audio/webm" }), "turn.webm");
          const { data } = await api.post("/wingman/listen", form, { timeout: 60000 });
          if (data.text?.trim()) await say(data.text);
          else toast.error("I did not catch that");
        } finally {
          setBusy(false);
        }
      };
      recorder.start();
      setTaping(true);
      setTimeout(() => recorder.state === "recording" && recorder.stop(), 15000);
    } catch {
      toast.error("MiLu needs the microphone for a practice call");
      setTaping(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/wingman/buddy/${code}/end`);
      setReport(data.report);
      if (!data.report) toast.success("Room closed — say a bit more next time for a report");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not write that up");
    } finally {
      setBusy(false);
    }
  };

  const link = `${window.location.origin}/buddy/${code}`;

  if (!code) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="buddy-start-page">
        <Link to="/progress" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Your practice
        </Link>
        <div className="mt-6 flex items-start gap-4">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/15 text-brand">
            <Users className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
              Practise with a friend
            </h1>
            <p className="mt-2 max-w-xl text-sm text-ink-soft md:text-base">
              Open a room, send the link to a friend, and talk in the language you are both learning.
              MiLu listens in and chips in every few turns — then writes you both a report.
            </p>
          </div>
        </div>

        <div className="mt-8 rounded-3xl border border-white/10 bg-panel p-5 sm:p-6">
          <label className="text-xs uppercase tracking-widest text-ink-soft">Language</label>
          <select
            data-testid="buddy-lang"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="mt-2 block w-full rounded-2xl border border-white/15 bg-surface px-4 py-3 text-sm text-ink outline-none focus:border-brand/60"
          >
            {LANGS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
          <button
            data-testid="buddy-open"
            onClick={open}
            disabled={busy}
            className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
            Open a practice room
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="buddy-room">
      <Link to="/progress" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Your practice
      </Link>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
          {room?.name || "Practice"} together
        </h1>
        <span data-testid="buddy-members"
              className="rounded-full border border-white/15 px-2.5 py-0.5 text-xs text-ink-soft">
          {(room?.members || []).map((m) => m.name).join(" · ") || "Just you so far"}
        </span>
        <button
          data-testid="buddy-copy"
          onClick={() => {
            navigator.clipboard.writeText(link);
            toast.success("Link copied — send it to your friend");
          }}
          className="ms-auto inline-flex items-center gap-1.5 rounded-full border border-brand/50 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/10"
        >
          <Copy className="h-3 w-3" /> Copy invite link
        </button>
      </div>

      <div ref={feed} data-testid="buddy-lines"
           className="mt-6 max-h-[46vh] space-y-2 overflow-y-auto rounded-3xl border border-white/10 bg-panel p-4">
        {(room?.lines || []).length === 0 && (
          <p className="text-sm text-ink-soft">
            Say hello in {room?.name || "your language"} — MiLu is listening.
          </p>
        )}
        {(room?.lines || []).map((l) => (
          <div key={`${l.at}-${l.name}`}
               className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                 l.who === "milu" ? "border border-brand/30 bg-brand/10 text-ink"
                   : l.who === user?.user_id ? "ms-auto bg-brand/15 text-ink" : "bg-surface text-ink"}`}>
            <p className={`text-[10px] ${l.who === "milu" ? "text-brand" : "text-ink-soft"}`}>
              {l.who === "milu" ? "MiLu" : l.name}
            </p>
            <p dir="auto">{l.text}</p>
          </div>
        ))}
      </div>

      {!report && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            data-testid="buddy-talk"
            onClick={talk}
            disabled={busy && !taping}
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-full border px-4 text-sm transition-colors ${
              taping ? "border-pink-400 bg-pink-500/15 text-pink-300"
                : "border-brand/50 text-brand hover:bg-brand/10"}`}
          >
            {busy && !taping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
            {taping ? "Stop and send" : "Hold the floor"}
          </button>
          <input
            data-testid="buddy-typed"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && typed.trim()) {
                say(typed);
                setTyped("");
              }
            }}
            placeholder="…or type it"
            className="min-w-[12rem] flex-1 rounded-full border border-white/15 bg-panel px-4 py-2.5 text-sm text-ink outline-none placeholder:text-ink-soft/70 focus:border-brand/60"
          />
          <button
            data-testid="buddy-end"
            onClick={finish}
            disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-destructive/50 px-4 text-sm text-destructive transition-colors hover:bg-destructive/10"
          >
            <PhoneOff className="h-4 w-4" /> End and get our report
          </button>
        </div>
      )}

      {report && (
        <div data-testid="buddy-report"
             className="mt-6 rounded-3xl border border-brand/30 bg-panel p-5">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand">
            <Sparkles className="h-3.5 w-3.5" /> Your {report.name} report
          </p>
          <p dir="auto" className="mt-2 text-sm text-ink-soft">{report.recap}</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {(report.people || []).map((p) => (
              <div key={p.name} data-testid={`buddy-person-${p.name}`}
                   className="rounded-2xl border border-white/10 bg-surface px-4 py-3">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-ink">{p.name}</p>
                  <span className="ms-auto rounded-full bg-brand/15 px-2 py-0.5 text-xs font-semibold text-brand">
                    {p.score}/100
                  </span>
                </div>
                {p.win && <p dir="auto" className="mt-1.5 text-xs text-emerald-300">✓ {p.win}</p>}
                {p.slip && <p dir="auto" className="mt-1 text-xs text-ink-soft">– {p.slip}</p>}
              </div>
            ))}
          </div>
          {report.practise && (
            <p dir="auto" className="mt-3 text-sm text-ink">
              Worth practising together: {report.practise}
            </p>
          )}
          <Link to="/progress" data-testid="buddy-to-progress"
                className="mt-4 inline-flex text-xs text-brand hover:underline">
            It is on your practice page →
          </Link>
        </div>
      )}
    </div>
  );
}
